<template>
  <div class="how-can-help-area ptb-100">
    <div class="container">
      <div class="section-title text-center">
        <span class="sub-title">Recursos Poderosos</span>
        <h2>Tudo que você precisa para gerenciar seu negócio</h2>
        <p>HawkCRM é um CRM/ERP completo e modular. Clique em cada recurso para saber mais.</p>
      </div>

      <!-- Categorias com Tabs -->
      <div class="categories-tabs" data-aos="fade-up">
        <button
            v-for="category in categories"
            :key="category.id"
            :class="['category-tab', { active: activeCategory === category.id }]"
            @click="activeCategory = category.id"
        >
          <i :class="category.icon"></i>
          {{ category.name }}
        </button>
      </div>

      <!-- Grid de Módulos -->
      <div class="modules-grid" data-aos="fade-up" data-aos-delay="100">
        <div
            v-for="module in filteredModules"
            :key="module.id"
            class="module-card"
            @click="openModal(module)"
        >
          <div class="module-icon">
            <i :class="module.icon"></i>
          </div>
          <h3>{{ module.name }}</h3>
          <p>{{ module.shortDesc }}</p>
          <div class="module-badge" v-if="module.badge">
            {{ module.badge }}
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <button class="modal-close" @click="closeModal">
            <i class="ph-x"></i>
          </button>

          <div class="modal-header">
            <div class="modal-icon">
              <i :class="selectedModule.icon"></i>
            </div>
            <h2>{{ selectedModule.name }}</h2>
            <p class="modal-category">{{ getCategoryName(selectedModule.category) }}</p>
          </div>

          <div class="modal-body">
            <p class="module-description">{{ selectedModule.fullDesc }}</p>

            <h3>Principais Funcionalidades:</h3>
            <ul class="features-list">
              <li v-for="(feature, index) in selectedModule.features" :key="index">
                <i class="ph-check-circle"></i>
                {{ feature }}
              </li>
            </ul>

            <div class="modal-footer">
              <a href="https://wa.me/5554992302401?text=Quero saber mais sobre o módulo {{ selectedModule.name }} do HawkCRM"
                 target="_blank"
                 class="btn-style-one green-color">
                <i class="flaticon-whatsapp"></i>
                Falar com Especialista
              </a>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'HowCanHelp',
  data() {
    return {
      activeCategory: 'core',
      showModal: false,
      selectedModule: {},

      categories: [
        { id: 'core', name: 'Core', icon: 'ph-package' },
        { id: 'gestao', name: 'Gestão', icon: 'ph-briefcase' },
        { id: 'vendas', name: 'Vendas & Marketing', icon: 'ph-chart-line-up' },
        { id: 'automacao', name: 'Automação', icon: 'ph-robot' },
        { id: 'comunicacao', name: 'Comunicação', icon: 'ph-chats' },
        { id: 'ecommerce', name: 'E-commerce', icon: 'ph-storefront' },
        { id: 'verticais', name: 'Verticais', icon: 'ph-stack' }
      ],

      modules: [
        // CORE
        {
          id: 1,
          category: 'core',
          name: 'Gestão de Clientes',
          icon: 'ph-users-three',
          badge: 'Core',
          shortDesc: 'CRM completo para gerenciar clientes e contatos',
          fullDesc: 'Gerencie todos os seus clientes, contatos, contratos e histórico de relacionamento em um só lugar.',
          features: [
            'Múltiplos contatos por cliente',
            'Portal exclusivo para clientes',
            'Histórico completo de interações',
            'Campos personalizados',
            'Permissões granulares',
            'Área separada cliente/admin'
          ]
        },
        {
          id: 2,
          category: 'core',
          name: 'Projetos e Tarefas',
          icon: 'ph-folder-notch-open',
          badge: 'Core',
          shortDesc: 'Gerenciamento completo de projetos',
          fullDesc: 'Gerencie projetos, tarefas, milestones e controle de tempo com Gantt Chart profissional.',
          features: [
            'Gantt Chart interativo',
            'Milestones e entregas',
            'Controle de tempo por tarefa',
            'Múltiplos membros por tarefa',
            'Seguidores de tarefas',
            'Tarefas recorrentes',
            'Vincular a propostas/invoices'
          ]
        },
        {
          id: 3,
          category: 'core',
          name: 'Faturas e Orçamentos',
          icon: 'ph-file-text',
          badge: 'Core',
          shortDesc: 'Invoices, estimativas e propostas',
          fullDesc: 'Crie invoices e orçamentos profissionais, receba pagamentos online e automatize recorrências.',
          features: [
            'Invoices recorrentes automáticas',
            'Múltiplas moedas',
            'Impostos por item',
            'Pagamentos online (Stripe, PayPal, etc)',
            'Conversão orçamento → invoice',
            'Lembretes automáticos',
            'Relatórios financeiros'
          ]
        },
        {
          id: 4,
          category: 'core',
          name: 'Sistema de Tickets',
          icon: 'ph-headset',
          badge: 'Core',
          shortDesc: 'Suporte ao cliente profissional',
          fullDesc: 'Sistema completo de tickets com auto-resposta, importação por email e prioridades.',
          features: [
            'Auto-importação por email/IMAP',
            'Respostas predefinidas',
            'Notas privadas da equipe',
            'Departamentos e atribuições',
            'Base de conhecimento integrada',
            'SLA e prioridades',
            'Clientes respondem por email'
          ]
        },
        {
          id: 5,
          category: 'core',
          name: 'Pipeline de Vendas',
          icon: 'ph-funnel',
          badge: 'Core',
          shortDesc: 'Gestão completa de leads',
          fullDesc: 'Gerencie leads, acompanhe conversões e automatize captura de novos leads.',
          features: [
            'Funil visual drag & drop',
            'Auto-importação de leads por email',
            'Web-to-lead forms ilimitados',
            'Notas e histórico completo',
            'Criar propostas direto do lead',
            'Importação CSV',
            'Relatórios de conversão'
          ]
        },
        {
          id: 6,
          category: 'core',
          name: 'Propostas Comerciais',
          icon: 'ph-file-pdf',
          badge: 'Core',
          shortDesc: 'Propostas profissionais que vendem',
          fullDesc: 'Crie propostas lindas para leads e clientes, receba notificações de aceite/rejeição.',
          features: [
            'Templates personalizáveis',
            'Assinatura digital',
            'Notificação de aceite/rejeição',
            'Email de agradecimento automático',
            'Aviso de vencimento',
            'Vincular a leads ou clientes'
          ]
        },

        // GESTÃO
        {
          id: 7,
          category: 'gestao',
          name: 'Gestão de Inventário',
          icon: 'ph-package',
          shortDesc: 'Controle completo de estoque',
          fullDesc: 'Gerencie produtos, estoque, fornecedores e movimentações com alertas automáticos.',
          features: [
            'Controle de estoque em tempo real',
            'Alertas de estoque mínimo',
            'Múltiplos depósitos',
            'Código de barras',
            'Histórico de movimentações',
            'Relatórios de inventário'
          ]
        },
        {
          id: 8,
          category: 'gestao',
          name: 'RH e Folha de Pagamento',
          icon: 'ph-user-circle',
          shortDesc: 'Gestão completa de recursos humanos',
          fullDesc: 'Controle de funcionários, ponto, férias, folha de pagamento e aprovações.',
          features: [
            'Cadastro completo de funcionários',
            'Controle de ponto e horas',
            'Gestão de férias e ausências',
            'Folha de pagamento',
            'Aprovações de despesas',
            'Documentos e contratos',
            'Avaliações de desempenho'
          ]
        },
        {
          id: 9,
          category: 'gestao',
          name: 'Gestão de Ativos',
          icon: 'ph-desktop-tower',
          shortDesc: 'Organize ativos da empresa e clientes',
          fullDesc: 'Controle equipamentos, veículos, licenças e outros ativos com manutenções programadas.',
          features: [
            'Cadastro de ativos',
            'Atribuição a funcionários/clientes',
            'Manutenções programadas',
            'Histórico completo',
            'Alertas de vencimento',
            'QR Code para rastreamento'
          ]
        },
        {
          id: 10,
          category: 'gestao',
          name: 'Gestão de Contratos',
          icon: 'ph-file-text',
          shortDesc: 'Controle de contratos e renovações',
          fullDesc: 'Gerencie todos os contratos com clientes, lembretes de vencimento e renovação automática.',
          features: [
            'PDFs profissionais',
            'Assinatura digital',
            'Lembretes de vencimento',
            'Renovação automática',
            'Templates personalizáveis',
            'Histórico e anexos'
          ]
        },

        // VENDAS & MARKETING
        {
          id: 11,
          category: 'vendas',
          name: 'Comissões de Vendas',
          icon: 'ph-percent',
          shortDesc: 'Cálculo automático de comissões',
          fullDesc: 'Calcule e gerencie comissões da equipe de vendas automaticamente.',
          features: [
            'Regras de comissão flexíveis',
            'Cálculo automático',
            'Múltiplos níveis',
            'Relatórios por vendedor',
            'Pagamento de comissões',
            'Metas e bônus'
          ]
        },
        {
          id: 12,
          category: 'vendas',
          name: 'Planejamento de Marketing',
          icon: 'ph-megaphone',
          shortDesc: 'Planeje e execute campanhas',
          fullDesc: 'Gerencie campanhas de marketing, ROI e resultados em um só lugar.',
          features: [
            'Calendário de campanhas',
            'Orçamento por campanha',
            'Tracking de resultados',
            'ROI e métricas',
            'Integração com analytics',
            'Relatórios visuais'
          ]
        },
        {
          id: 13,
          category: 'vendas',
          name: 'Google Lead Finder',
          icon: 'ph-magnifying-glass',
          shortDesc: 'Encontre leads no Google',
          fullDesc: 'Encontre e importe leads diretamente do Google para seu CRM.',
          features: [
            'Busca automática de leads',
            'Importação com um clique',
            'Enriquecimento de dados',
            'Filtros avançados',
            'Geolocalização'
          ]
        },
        {
          id: 14,
          category: 'vendas',
          name: 'Integração Facebook Leads',
          icon: 'ph-facebook-logo',
          shortDesc: 'Sincronize leads do Facebook',
          fullDesc: 'Importe automaticamente leads dos seus anúncios do Facebook.',
          features: [
            'Sincronização automática',
            'Formulários do Facebook',
            'Atribuição de responsável',
            'Notificações em tempo real'
          ]
        },

        // AUTOMAÇÃO
        {
          id: 15,
          category: 'automacao',
          name: 'Automação de Workflow',
          icon: 'ph-flow-arrow',
          shortDesc: 'Automatize processos complexos',
          fullDesc: 'Crie fluxos de trabalho automáticos sem programar, economize tempo e elimine erros.',
          features: [
            'Editor visual de workflows',
            'Triggers personalizados',
            'Ações em sequência',
            'Condições e regras',
            'Notificações automáticas',
            'Integrações múltiplas'
          ]
        },
        {
          id: 16,
          category: 'automacao',
          name: 'API REST Completa',
          icon: 'ph-code',
          shortDesc: 'Integre com qualquer sistema',
          fullDesc: 'API REST completa para integrar HawkCRM com seus sistemas.',
          features: [
            'Endpoints completos',
            'Documentação detalhada',
            'Autenticação segura',
            'Rate limiting',
            'Webhooks',
            'SDKs disponíveis'
          ]
        },
        {
          id: 17,
          category: 'automacao',
          name: 'Integração Zapier',
          icon: 'ph-lightning',
          shortDesc: 'Conecte 5000+ apps',
          fullDesc: 'Integre HawkCRM com mais de 5000 aplicativos via Zapier.',
          features: [
            'Triggers e ações',
            'Zaps multi-etapas',
            'Sem código',
            'Templates prontos',
            'Sincronização bidirecional'
          ]
        },
        {
          id: 18,
          category: 'automacao',
          name: 'Webhooks',
          icon: 'ph-git-fork',
          shortDesc: 'Notificações em tempo real',
          fullDesc: 'Receba notificações instantâneas de eventos do CRM em seus sistemas.',
          features: [
            'Eventos personalizáveis',
            'Payload completo',
            'Retry automático',
            'Logs detalhados',
            'Assinatura de requisições'
          ]
        },

        // COMUNICAÇÃO
        {
          id: 19,
          category: 'comunicacao',
          name: 'WhatsApp Business',
          icon: 'ph-whatsapp-logo',
          shortDesc: 'Marketing e atendimento via WhatsApp',
          fullDesc: 'Integração oficial com WhatsApp Business API para marketing e atendimento.',
          features: [
            'API oficial WhatsApp',
            'Envio em massa',
            'Templates aprovados',
            'Chatbot com IA',
            'Múltiplos atendentes',
            'Histórico completo',
            'Analytics detalhado'
          ]
        },
        {
          id: 20,
          category: 'comunicacao',
          name: 'MailChimp',
          icon: 'ph-envelope',
          shortDesc: 'Email marketing profissional',
          fullDesc: 'Integração com MailChimp para campanhas de email marketing.',
          features: [
            'Sincronização de contatos',
            'Campanhas automatizadas',
            'Segmentação avançada',
            'Relatórios de abertura',
            'A/B testing'
          ]
        },

        // E-COMMERCE
        {
          id: 21,
          category: 'ecommerce',
          name: 'Loja Virtual Completa',
          icon: 'ph-storefront',
          shortDesc: 'E-commerce integrado ao CRM',
          fullDesc: 'Loja virtual completa com carrinho, pagamentos e gestão de produtos.',
          features: [
            'Catálogo de produtos',
            'Carrinho de compras',
            'Múltiplos gateways',
            'Gestão de estoque',
            'Cupons de desconto',
            'Fretes integrados',
            'PDV (POS) completo'
          ]
        },

        // VERTICAIS
        {
          id: 22,
          category: 'verticais',
          name: 'Gestão Hoteleira',
          icon: 'ph-bed',
          shortDesc: 'Sistema completo para hotéis',
          fullDesc: 'Gerencie reservas, check-in/out, housekeeping e faturamento hoteleiro.',
          features: [
            'Reservas online',
            'Check-in/Check-out',
            'Gestão de quartos',
            'Housekeeping',
            'Channel manager',
            'Faturamento integrado'
          ]
        },
        {
          id: 23,
          category: 'verticais',
          name: 'Imobiliária',
          icon: 'ph-house',
          shortDesc: 'CRM para mercado imobiliário',
          fullDesc: 'Gerencie imóveis, proprietários, inquilinos e contratos.',
          features: [
            'Catálogo de imóveis',
            'Portal para clientes',
            'Contratos de locação',
            'Gestão de inquilinos',
            'Boletos automáticos',
            'Vistorias digitais'
          ]
        },
        {
          id: 24,
          category: 'verticais',
          name: 'Eventos e Ingressos',
          icon: 'ph-ticket',
          shortDesc: 'Gestão completa de eventos',
          fullDesc: 'Crie eventos, venda ingressos online e gerencie participantes.',
          features: [
            'Criação de eventos',
            'Venda de ingressos',
            'QR Code check-in',
            'Certificados digitais',
            'Relatórios financeiros',
            'Múltiplos tipos de ingresso'
          ]
        },
        {
          id: 25,
          category: 'verticais',
          name: 'Logística e Entregas',
          icon: 'ph-truck',
          shortDesc: 'Gestão de frota e entregas',
          fullDesc: 'Controle entregas, rotas, veículos e motoristas.',
          features: [
            'Rastreamento em tempo real',
            'Otimização de rotas',
            'Gestão de frota',
            'Controle de motoristas',
            'Proof of delivery',
            'Custos por entrega'
          ]
        },
        {
          id: 26,
          category: 'verticais',
          name: 'Manufatura',
          icon: 'ph-factory',
          shortDesc: 'MRP para indústrias',
          fullDesc: 'Planejamento de produção, BOM, ordens de produção e controle de qualidade.',
          features: [
            'Ordens de produção',
            'Bill of Materials (BOM)',
            'Planejamento (MRP)',
            'Controle de qualidade',
            'Custos de produção',
            'Rastreabilidade'
          ]
        },
        {
          id: 27,
          category: 'verticais',
          name: 'Gestão de Aluguel',
          icon: 'ph-key',
          shortDesc: 'Aluguel de equipamentos',
          fullDesc: 'Gerencie aluguel de equipamentos, veículos ou qualquer ativo.',
          features: [
            'Catálogo de itens',
            'Reservas e disponibilidade',
            'Contratos de locação',
            'Calendário visual',
            'Cálculo automático de valores',
            'Multas e devoluções'
          ]
        }
      ]
    }
  },
  computed: {
    filteredModules() {
      return this.modules.filter(m => m.category === this.activeCategory)
    }
  },
  methods: {
    openModal(module) {
      this.selectedModule = module
      this.showModal = true
      document.body.style.overflow = 'hidden'
    },
    closeModal() {
      this.showModal = false
      document.body.style.overflow = 'auto'
    },
    getCategoryName(categoryId) {
      const cat = this.categories.find(c => c.id === categoryId)
      return cat ? cat.name : ''
    }
  }
}
</script>

<style scoped>
/* ===== AREA BASE ===== */
.how-can-help-area {
  background: #f9f9f9;
}

/* ===== TABS DE CATEGORIAS ===== */
.categories-tabs {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 50px;
}

.category-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
}

.category-tab i {
  font-size: 20px;
}

.category-tab:hover {
  border-color: #6D5DFC;
  color: #6D5DFC;
  transform: translateY(-2px);
}

.category-tab.active {
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  border-color: #6D5DFC;
  color: white;
  box-shadow: 0 4px 15px rgba(109, 93, 252, 0.3);
}

/* ===== GRID DE MÓDULOS ===== */
.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
}

.module-card {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  border: 2px solid transparent;
}

.module-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border-color: #6D5DFC;
}

.module-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, rgba(109, 93, 252, 0.1) 0%, rgba(67, 97, 238, 0.1) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.module-icon i {
  font-size: 32px;
  color: #6D5DFC;
}

.module-card h3 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #1a1a1a;
}

.module-card p {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

.module-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

/* ===== MODAL ===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: rotate(90deg);
}

.modal-close i {
  font-size: 24px;
}

.modal-header {
  padding: 40px 40px 30px;
  text-align: center;
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  color: white;
  border-radius: 20px 20px 0 0;
}

.modal-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  backdrop-filter: blur(10px);
}

.modal-icon i {
  font-size: 40px;
  color: white;
}

.modal-header h2 {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 10px;
  color: white;
}

.modal-category {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
}

.modal-body {
  padding: 40px;
}

.module-description {
  font-size: 16px;
  line-height: 1.7;
  color: #666;
  margin-bottom: 30px;
}

.modal-body h3 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #1a1a1a;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 30px;
  display: grid;
  gap: 12px;
}

.features-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  color: #444;
  line-height: 1.6;
}

.features-list i {
  font-size: 22px;
  color: #25D366;
  flex-shrink: 0;
  margin-top: 2px;
}

.modal-footer {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.btn-style-one {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 15px 30px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-style-one:hover {
  background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
  color: white;
}

/* Modal Animation */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-fade-enter-from .modal-container,
.modal-fade-leave-to .modal-container {
  transform: scale(0.9);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .categories-tabs {
    gap: 8px;
  }

  .category-tab {
    padding: 10px 16px;
    font-size: 13px;
  }

  .category-tab i {
    font-size: 16px;
  }

  .modules-grid {
    grid-template-columns: 1fr;
  }

  .modal-header,
  .modal-body {
    padding: 30px 20px;
  }

  .modal-header h2 {
    font-size: 24px;
  }
}
</style>